/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts
 🗓️ Created: 2025-12-23
 🧩 Description:
    API client for SIP Endpoints module.
 ────────────────────────────────────────────────────────────────*/

import { api } from '../../lib/api';

/**
 * SIP Registration entry
 */
export interface SipRegistration {
    reg_user: string;
    realm: string;
    token: string;
    url: string;
    expires: number;
    expires_formatted: string;
    network_ip: string;
    network_port: number;
    network_proto: string;
    hostname: string;
    metadata: string | null;
    status: 'registered' | 'expired';
}

/**
 * SIP Endpoints statistics
 */
export interface SipEndpointsStats {
    total: number;
    byProtocol: { [key: string]: number };
    byHostname: { [key: string]: number };
}

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
    success: boolean;
    data: T;
    pagination?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

/**
 * Fetch SIP endpoints (registrations)
 */
export const fetchSipEndpoints = async (params: {
    page?: number;
    limit?: number;
    search?: string;
    domainId?: number;
}): Promise<ApiResponse<SipRegistration[]>> => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.search) queryParams.set('search', params.search);
    if (params.domainId != null) queryParams.set('domainId', String(params.domainId));

    const response = await api.get<ApiResponse<SipRegistration[]>>(
        `/api/telephony/sip-endpoints?${queryParams.toString()}`
    );
    return response.data;
};

/**
 * Fetch SIP endpoints statistics
 */
export const fetchSipEndpointsStats = async (): Promise<SipEndpointsStats> => {
    const response = await api.get<ApiResponse<SipEndpointsStats>>(
        '/api/telephony/sip-endpoints/stats'
    );
    return response.data.data;
};

/**
 * Refresh SIP endpoints data
 */
export const refreshSipEndpoints = async (): Promise<{ message: string; count: number }> => {
    const response = await api.post<ApiResponse<{ message: string; count: number }>>(
        '/api/telephony/sip-endpoints/refresh'
    );
    return response.data.data;
};
